import {
  computePosition,
  autoUpdate,
  size,
  flip,
  type ComputePositionConfig,
} from '@floating-ui/dom'
import { deepmerge } from 'deepmerge-ts'
import { useEffect, useMemo, useState } from 'react'

const defaultOptions = (minContentWidth = 200): ComputePositionConfig => ({
  strategy: 'fixed',
  placement: 'bottom-start',
  middleware: [
    flip(),
    size({
      altBoundary: true,
      apply({ availableWidth, rects, elements }) {
        Object.assign(elements.floating.style, {
          minWidth: `${rects.reference.width}px`,
          // Equivalent to the CSS function `fit-content(<reference width>)`
          maxWidth: `${Math.min(Math.max(minContentWidth, rects.reference.width), availableWidth)}px`,
        })
      },
    }),
  ],
})

type UseDropdownPositioningHook = (
  referenceElement: HTMLElement | null,
  contentElement: HTMLElement | null,
  options?: {
    /* Override options passed to `computePosition`. */
    computePosition?: Partial<ComputePositionConfig>
    /**
     * Overrides the minimum width of the content.
     * If the reference is narrower than the content, then the content width
     * is clamped to this value (in pixels.) Defaults to 200px.
     *
     * Note that the content will always be as wide as the reference. If this
     * value is smaller than the reference width, then the content width will
     * be clamped to the reference width.
     */
    minContentWidth?: number
  }
) => { isPositioned: boolean }

/**
 * Provides automatic positioning for dropdown menu components, using Floating UI.
 *
 * Some styling is required on your component:
 *  * The `contentElement` must have a `position` rule matching the
 *    `strategy` option (`fixed` by default.)
 *  * To avoid layout inference, also set `width: max-content; top: 0; left: 0;`
 *
 * @param referenceElement The reference/trigger element (usually the button that opens the menu.)
 * @param contentElement The menu element which should open and close.
 * @param options Floating-UI options to override.
 *
 * @see https://floating-ui.com/docs/computeposition#initial-layout
 */
export const useDropdownPositioning: UseDropdownPositioningHook = (
  referenceElement,
  contentElement,
  options
) => {
  const [isPositioned, setIsPositioned] = useState(false)
  const mergedOptions = useMemo(
    () =>
      deepmerge(
        defaultOptions(options?.minContentWidth),
        options?.computePosition ?? {}
      ),
    [options?.computePosition, options?.minContentWidth]
  )

  useEffect(() => {
    if (!referenceElement || !contentElement) {
      return
    }

    const cleanup = autoUpdate(referenceElement, contentElement, async () => {
      const { x, y } = await computePosition(
        referenceElement,
        contentElement,
        mergedOptions
      )

      Object.assign(contentElement.style, {
        left: `${x}px`,
        top: `${y}px`,
      })

      setIsPositioned(true)
    })

    return () => {
      if (contentElement) {
        Object.assign(contentElement.style, {
          left: '',
          top: '',
        })
      }

      setIsPositioned(false)
      cleanup()
    }
  }, [referenceElement, contentElement, mergedOptions])

  return { isPositioned }
}
